import type { FC } from 'react';
import type { useEditor } from '@tiptap/react';
import { EditorContent } from '@tiptap/react';
import { Input, Select, Switch, Button } from 'antd';
import { Loader2, Languages, ExternalLink } from 'lucide-react';
import TiptapToolbar from './TiptapToolbar';

const PERSONA_OPTIONS = [
  { value: 'developer', label: '👨‍💻 Développeur' },
  { value: 'cto', label: '🏗️ CTO / Fondateur' },
  { value: 'product_manager', label: '📋 Product Manager' },
  { value: 'qa_lead', label: '🧪 QA Lead' },
  { value: 'support', label: '🎧 Support Client' },
  { value: 'custom', label: '✏️ Personnalisé...' },
];

const LOCALE_OPTIONS = [
  { value: 'fr', label: '🇫🇷 Français' },
  { value: 'en', label: '🇬🇧 English' },
];

interface BlogEditorFormProps {
  title: string;
  slug: string;
  locale: string;
  persona: string;
  customPersona: string;
  autoTranslate: boolean;
  isEdit: boolean;
  translating: boolean;
  detailLoading: boolean;
  otherLocaleLabel: string;
  landingUrl: string;
  editor: ReturnType<typeof useEditor>;
  initialArticle: unknown;
  onTitleChange: (value: string) => void;
  onSlugChange: (value: string) => void;
  onLocaleChange: (value: string) => void;
  onPersonaChange: (value: string) => void;
  onCustomPersonaChange: (value: string) => void;
  onAutoTranslateChange: (value: boolean) => void;
  onTranslateOrSync: () => void;
}

const BlogEditorForm: FC<BlogEditorFormProps> = ({
  title,
  slug,
  locale,
  persona,
  customPersona,
  autoTranslate,
  isEdit,
  translating,
  detailLoading,
  otherLocaleLabel,
  landingUrl,
  editor,
  initialArticle,
  onTitleChange,
  onSlugChange,
  onLocaleChange,
  onPersonaChange,
  onCustomPersonaChange,
  onAutoTranslateChange,
  onTranslateOrSync,
}) => (
  <>
    {detailLoading && (
      <div className="flex items-center gap-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
        <Loader2 className="h-4 w-4 animate-spin" /> Chargement...
      </div>
    )}

    {/* Titre + Slug */}
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">Titre</label>
      <Input value={title} onChange={(e) => onTitleChange(e.target.value)} placeholder="Titre de l'article" size="large" />
    </div>
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">Slug URL</label>
      <Input value={slug} onChange={(e) => onSlugChange(e.target.value)} addonBefore="blog/" disabled={isEdit} />
    </div>

    {/* Locale + Auto-translate + Sync + Preview */}
    <div className="flex items-end gap-3 flex-wrap">
      <div className="w-36">
        <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">Langue</label>
        <Select value={locale} onChange={onLocaleChange} className="w-full" options={LOCALE_OPTIONS} disabled={!!initialArticle} />
      </div>
      <div className="flex items-center gap-2 pb-1">
        <Switch size="small" checked={autoTranslate} onChange={onAutoTranslateChange} />
        <span className="text-xs text-slate-500 dark:text-slate-400">Auto-traduction → {otherLocaleLabel}</span>
      </div>
      {isEdit && (
        <Button
          size="small"
          icon={translating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Languages className="h-3.5 w-3.5" />}
          onClick={onTranslateOrSync}
          disabled={translating}
          className="mb-0.5"
        >
          {translating ? 'Traduction IA...' : `🔄 Sync → ${otherLocaleLabel}`}
        </Button>
      )}
      {isEdit && (
        <Button
          type="link"
          size="small"
          icon={<ExternalLink className="h-3.5 w-3.5" />}
          onClick={() => window.open(`${landingUrl}/${locale}/blog/${slug}`, '_blank')}
          className="pb-0.5"
        >
          Prévisualiser
        </Button>
      )}
    </div>

    {/* Persona */}
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">Persona cible</label>
      <div className="flex gap-2">
        <Select value={persona} onChange={onPersonaChange} className="flex-1" options={PERSONA_OPTIONS} />
        {persona === 'custom' && (
          <Input
            value={customPersona}
            onChange={(e) => onCustomPersonaChange(e.target.value)}
            placeholder="Ex: Growth Marketer en SaaS B2B"
            className="flex-1"
          />
        )}
      </div>
    </div>

    {/* TipTap Editor */}
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">Contenu</label>
      <div className="rounded-lg border border-slate-200 dark:border-slate-600 overflow-hidden">
        <TiptapToolbar editor={editor} />
        <EditorContent editor={editor} />
      </div>
    </div>
  </>
);

export default BlogEditorForm;
